import { useEffect, useRef, useState } from "react";

const THRESHOLD = 70;
const MAX_PULL = 110;

// iOS home-screen apps have no native pull-to-refresh (Android Chrome provides its own).
export function needsPullToRefresh() {
  const ios = /iPhone|iPad|iPod/.test(navigator.userAgent)
    || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const standalone = window.navigator.standalone === true
    || window.matchMedia?.("(display-mode: standalone)").matches;
  return ios && standalone;
}

function isTyping() {
  const el = document.activeElement;
  return el && (["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName) || el.isContentEditable);
}

export default function PullToRefresh({ scrollRef, color }) {
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const start = useRef(null);
  const pullRef = useRef(0);

  useEffect(() => {
    const atTop = () => window.scrollY <= 0 && (scrollRef?.current?.scrollTop || 0) <= 0;
    const onStart = e => {
      start.current = e.touches.length === 1 && atTop() && !isTyping() ? e.touches[0].clientY : null;
    };
    const onMove = e => {
      if (start.current === null) return;
      const dy = e.touches[0].clientY - start.current;
      pullRef.current = dy <= 0 || !atTop() ? 0 : Math.min(dy * 0.5, MAX_PULL);
      setPull(pullRef.current);
    };
    const onEnd = () => {
      if (start.current === null) return;
      start.current = null;
      const pulled = pullRef.current;
      pullRef.current = 0;
      setPull(0);
      if (pulled >= THRESHOLD) {
        setRefreshing(true);
        window.location.reload();
      }
    };
    document.addEventListener("touchstart", onStart, { passive: true });
    document.addEventListener("touchmove", onMove, { passive: true });
    document.addEventListener("touchend", onEnd, { passive: true });
    document.addEventListener("touchcancel", onEnd, { passive: true });
    return () => {
      document.removeEventListener("touchstart", onStart);
      document.removeEventListener("touchmove", onMove);
      document.removeEventListener("touchend", onEnd);
      document.removeEventListener("touchcancel", onEnd);
    };
  }, [scrollRef]);

  const shown = refreshing ? THRESHOLD : pull;
  if (!shown) return null;
  const ready = shown >= THRESHOLD;
  return (
    <div style={{
      position: "fixed", left: "50%", top: "calc(env(safe-area-inset-top, 0px) + 8px)", zIndex: 200,
      transform: `translate(-50%, ${shown - 40}px)`, opacity: Math.min(shown / THRESHOLD, 1),
      width: 36, height: 36, borderRadius: "50%", background: "#fff", boxShadow: "0 2px 10px rgba(0,0,0,0.25)",
      display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none",
    }}>
      <span style={{
        fontSize: 20, lineHeight: 1, color: ready ? color : "#888", fontWeight: 700,
        transform: `rotate(${refreshing ? 0 : shown * 3}deg)`,
        animation: refreshing ? "ptr-spin 0.8s linear infinite" : "none",
      }}>↻</span>
      <style>{"@keyframes ptr-spin { to { transform: rotate(360deg); } }"}</style>
    </div>
  );
}
